import { Token } from '@uniswap/sdk-core'
import { useMemo } from 'react'
import { useCommonTokensOptions } from 'uniswap/src/components/TokenSelector/hooks/useCommonTokensOptions'
import { useCurrencies } from 'uniswap/src/components/TokenSelector/hooks/useCurrencies'
import {
  currencyInfosToTokenOptions,
  useCurrencyInfosToTokenOptions,
} from 'uniswap/src/components/TokenSelector/hooks/useCurrencyInfosToTokenOptions'
import { TokenOption } from 'uniswap/src/components/lists/items/types'
import { COMMON_BASES } from 'uniswap/src/constants/routing'
import { FEW_WRAPPED_NATIVE_CURRENCY, USDR } from 'uniswap/src/constants/tokens'
import { useUniswapContextSelector } from 'uniswap/src/contexts/UniswapContext'
import { GqlResult } from 'uniswap/src/data/types'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import { PortfolioBalance } from 'uniswap/src/features/dataApi/types'
import { getPreferredTokenLogoUrl } from 'uniswap/src/features/tokens/getPreferredTokenLogoUrl'
import { currencyId } from 'uniswap/src/utils/currencyId'

const EMPTY_PORTFOLIO_BALANCES: Record<string, PortfolioBalance> = {}
const EXCLUDED_QUICK_SELECT_CURRENCY_IDS = new Set(
  [USDR, FEW_WRAPPED_NATIVE_CURRENCY[UniverseChainId.Mainnet]]
    .filter((currency): currency is Token => Boolean(currency))
    .map((currency) => currencyId(currency).toLowerCase()),
)

export function useCommonTokensOptionsWithFallback(
  address: Address | undefined,
  chainFilter: UniverseChainId | null,
): GqlResult<TokenOption[] | undefined> {
  const searchLocalTokenSelectorResults = useUniswapContextSelector((ctx) => ctx.searchLocalTokenSelectorResults)
  const { data, error, refetch, loading } = useCommonTokensOptions(address, chainFilter)
  const commonBases = useMemo(
    () => (chainFilter ? currencyInfosToTokenOptions(COMMON_BASES[chainFilter]) : undefined),
    [chainFilter],
  )
  const commonBasesCurrencyIds = useMemo(
    () => commonBases?.map((token) => currencyId(token.currencyInfo.currency)).filter(Boolean) ?? [],
    [commonBases],
  )
  const { data: commonBasesCurrencies } = useCurrencies(commonBasesCurrencyIds)
  const commonBasesTokenOptions = useCurrencyInfosToTokenOptions({
    currencyInfos: commonBasesCurrencies,
    portfolioBalancesById: EMPTY_PORTFOLIO_BALANCES,
  })
  const shouldFallback = data?.length === 0 && commonBases?.length
  const shouldMergeCommonBases = chainFilter === UniverseChainId.RobinhoodChain

  const dataWithCommonBases = useMemo(() => {
    if (!shouldMergeCommonBases || !commonBases?.length) {
      if (shouldFallback) {
        return commonBasesTokenOptions
      }
      return data
    }

    const merged = new Map(
      commonBases.map((token) => [currencyId(token.currencyInfo.currency).toLowerCase(), token] as const),
    )
    for (const token of data ?? []) {
      // Prefer API-backed options so balances and richer metadata are preserved.
      merged.set(currencyId(token.currencyInfo.currency).toLowerCase(), token)
    }
    return Array.from(merged.values())
  }, [commonBases, commonBasesTokenOptions, data, shouldFallback, shouldMergeCommonBases])

  const dataWithResolvedLogos = useMemo(
    () =>
      dataWithCommonBases
        ?.filter(
          (tokenOption) =>
            !EXCLUDED_QUICK_SELECT_CURRENCY_IDS.has(currencyId(tokenOption.currencyInfo.currency).toLowerCase()),
        )
        .map((tokenOption) => {
          const { currencyInfo } = tokenOption
          const { currency } = currencyInfo
          if (currency.isNative) {
            const canonicalNativeLogo = COMMON_BASES[currency.chainId]?.find(
              (commonBase) => commonBase.currency.isNative,
            )?.logoUrl
            return canonicalNativeLogo
              ? { ...tokenOption, currencyInfo: { ...currencyInfo, logoUrl: canonicalNativeLogo } }
              : tokenOption
          }

          const localCurrencyInfo = searchLocalTokenSelectorResults?.({
            chainFilter: currency.chainId as UniverseChainId,
            searchFilter: currency.address,
          })?.find(
            (localInfo) =>
              localInfo.currency.chainId === currency.chainId &&
              localInfo.currency.isToken &&
              localInfo.currency.address.toLowerCase() === currency.address.toLowerCase(),
          )
          const logoUrl = getPreferredTokenLogoUrl(currencyInfo, localCurrencyInfo?.logoUrl)

          return logoUrl === currencyInfo.logoUrl
            ? tokenOption
            : { ...tokenOption, currencyInfo: { ...currencyInfo, logoUrl } }
        }),
    [dataWithCommonBases, searchLocalTokenSelectorResults],
  )

  return useMemo(
    () => ({
      data: dataWithResolvedLogos,
      error: dataWithResolvedLogos?.length ? undefined : error,
      refetch,
      loading,
    }),
    [dataWithResolvedLogos, error, loading, refetch],
  )
}
